import { useEffect, useRef, useState } from 'react'
import { AlertTriangle } from 'lucide-react'

interface SandboxedWidgetProps {
  widget: any
  isExpanded?: boolean
}

/**
 * Sandboxed Widget Component
 * 
 * Renders third-party widgets in an isolated iframe to prevent:
 * - XSS attacks
 * - Data theft (localStorage, cookies)
 * - DOM manipulation
 * - Session hijacking
 * 
 * Communication happens via postMessage API only.
 */
export default function SandboxedWidget({ widget, isExpanded = false }: SandboxedWidgetProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [hasError, setHasError] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [resourceViolation, setResourceViolation] = useState(false)

  // Resource monitoring
  const loadTimeoutRef = useRef<number>()
  const healthCheckRef = useRef<number>()

  useEffect(() => {
    // Set up widget loading timeout (30 seconds)
    loadTimeoutRef.current = setTimeout(() => {
      if (isLoading) {
        setHasError(true)
        setErrorMessage('Widget failed to load (timeout)')
        setIsLoading(false)
      }
    }, 30000)

    // Set up postMessage listener for widget communication
    const handleMessage = (event: MessageEvent) => {
      // Verify origin for security
      const allowedOrigins = [
        window.location.origin,
        import.meta.env.VITE_WIDGET_DOMAIN || 'http://localhost:8000'
      ]

      if (!allowedOrigins.includes(event.origin)) {
        console.warn('Blocked message from untrusted origin:', event.origin)
        return
      }

      // Handle different message types
      switch (event.data.type) {
        case 'widget:loaded':
          setIsLoading(false)
          if (loadTimeoutRef.current) {
            clearTimeout(loadTimeoutRef.current)
          }
          console.log(`Widget ${widget.app_slug} loaded successfully`)
          break

        case 'widget:error':
          setHasError(true)
          setErrorMessage(event.data.message || 'Widget error')
          setIsLoading(false)
          break

        case 'widget:api-request':
          // Proxy API request through backend
          handleAPIRequest(event.data.payload)
          break

        case 'widget:ready':
          // Send widget configuration
          sendToWidget({
            type: 'config',
            payload: {
              widgetId: widget.id,
              settings: widget.config || {},
              isExpanded
            }
          })
          break

        case 'widget:resource-violation':
          setResourceViolation(true)
          console.error('Widget resource violation:', event.data.message)
          break

        default:
          console.log('Unhandled widget message:', event.data)
      }
    }

    window.addEventListener('message', handleMessage)

    // Health check - ping widget every 10 seconds
    healthCheckRef.current = setInterval(() => {
      sendToWidget({ type: 'ping' })
    }, 10000)

    return () => {
      window.removeEventListener('message', handleMessage)
      if (loadTimeoutRef.current) {
        clearTimeout(loadTimeoutRef.current)
      }
      if (healthCheckRef.current) {
        clearInterval(healthCheckRef.current)
      }
    }
  }, [widget.id, widget.app_slug, isExpanded])

  /**
   * Send message to sandboxed widget
   */
  const sendToWidget = (message: any) => {
    if (iframeRef.current && iframeRef.current.contentWindow) {
      iframeRef.current.contentWindow.postMessage(message, '*')
    }
  }

  /**
   * Handle API requests from widget (proxy through backend)
   */
  const handleAPIRequest = async (payload: any) => {
    try {
      const response = await fetch('/api/widget-proxy', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth-storage')}`
        },
        body: JSON.stringify({
          widgetId: widget.id,
          url: payload.url,
          method: payload.method || 'GET',
          data: payload.data,
          requestId: payload.requestId
        })
      })

      const data = await response.json()

      // Send response back to widget
      sendToWidget({
        type: 'widget:api-response',
        payload: {
          requestId: payload.requestId,
          data,
          status: response.status
        }
      })
    } catch (error: any) {
      // Send error back to widget
      sendToWidget({
        type: 'widget:api-response',
        payload: {
          requestId: payload.requestId,
          error: error.message,
          status: 500
        }
      })
    }
  }

  /**
   * Get widget source URL
   */
  const getWidgetUrl = () => {
    const widgetDomain = import.meta.env.VITE_WIDGET_DOMAIN || window.location.origin
    return `${widgetDomain}/widgets/${widget.app_slug}/index.html`
  }

  // Error state
  if (hasError) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-6 text-center">
        <AlertTriangle size={48} className="text-red-500 mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Widget Error</h3>
        <p className="text-sm text-gray-600 mb-4">{errorMessage}</p>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
        >
          Reload Page
        </button>
      </div>
    )
  }

  // Resource violation warning
  if (resourceViolation) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-6 text-center bg-yellow-50 border-2 border-yellow-400">
        <AlertTriangle size={48} className="text-yellow-600 mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Widget Terminated</h3>
        <p className="text-sm text-gray-600 mb-4">
          This widget exceeded resource limits and was automatically terminated to protect your system.
        </p>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition"
        >
          Reload Page
        </button>
      </div>
    )
  }

  return (
    <div className="relative w-full h-full">
      {/* Loading indicator */}
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-50 z-10">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-sm text-gray-600">Loading {widget.app_name}...</p>
          </div>
        </div>
      )}

      {/* Sandboxed iframe */}
      <iframe
        ref={iframeRef}
        src={getWidgetUrl()}
        title={widget.app_name}
        sandbox="allow-scripts allow-same-origin"
        allow="none"
        style={{
          width: '100%',
          height: '100%',
          border: 'none',
          display: isLoading ? 'none' : 'block'
        }}
        onLoad={() => {
          console.log(`Widget iframe loaded: ${widget.app_slug}`)
        }}
        onError={() => {
          setHasError(true)
          setErrorMessage('Failed to load widget iframe')
          setIsLoading(false)
        }}
      />

      {/* Security badge */}
      {!isLoading && !hasError && (
        <div
          className="absolute top-2 right-2 bg-green-100 text-green-700 text-xs px-2 py-1 rounded-full flex items-center gap-1 opacity-70 hover:opacity-100 transition z-20"
          title="This widget runs in a secure sandbox"
        >
          🔒 Sandboxed
        </div>
      )}
    </div>
  )
}

